import React, { useEffect, useRef, useState } from 'react';
import { difference } from 'lodash';
import { NavigationControl } from 'maplibre-gl';
import {
  initMap,
  Map,
  Marker,
  MarkerIconOptions,
  MarkerRenderers,
} from '@glympse/map-core';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@glympse/map-core/index.css';
import EnvironmentUtils from '../../utils/environment-utils';
import AuthUtils from '../../utils/auth-utils';
import './Glympser.scss';

export interface MapViewerInvite {
  id: string;
  [key: string]: any;
}

export interface GlympserMarker {
  lat: number;
  lng: number;
  icon?: Partial<MarkerIconOptions>;
}

export interface GlympserProps {
  center?: { lat: number; lng: number };
  markers?: GlympserMarker[];
  invites: string[];
  markerRenderers?: MarkerRenderers;
  onMapReady?: (map: Map) => void;
}

export default function Glympser(props: GlympserProps) {
  const { center, markers = [], invites = [], markerRenderers, onMapReady } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const invitesRef = useRef<string[]>([]);
  const [map, setMap] = useState<Map>(null);

  useEffect(() => {
    const instance = initMap({
      el: containerRef.current,
      center: center ? [center.lng, center.lat] : undefined,
      zoom: center ? 10 : 3,
      tiles: { provider: 'here-vector', apiKey: EnvironmentUtils.getVar('HERE_MAPS_APIKEY') },
      api: {
        coreBaseUrl: `${EnvironmentUtils.getVar('CORE_API')}/`,
        coreToken: AuthUtils.getAuthToken(),
      },
      controls: [new NavigationControl({ showCompass: false })],
      markers: markerRenderers,
      autoFocus: false,
    });

    setMap(instance);
    onMapReady?.(instance);

    return () => {
      // map-core doesn't stop invite polling on dispose
      invitesRef.current.forEach((code) => instance.removeInvite(code));
      invitesRef.current = [];
      instance.dispose();
    };
  }, []);

  const markersKey = JSON.stringify(markers);

  useEffect(() => {
    if (!map) return undefined;

    const features = markers
      .filter((marker) => marker.lat && marker.lng)
      .map((marker) => new Marker({
        lngLat: [marker.lng, marker.lat],
        icon: marker.icon,
        anchor: 'bottom',
        clickable: false,
      }));

    features.forEach((feature) => map.addFeature(feature));

    return () => features.forEach((feature) => map.removeFeature(feature));
  }, [map, markersKey]);

  useEffect(() => {
    if (!map) return;

    const current = invites.filter(Boolean);
    difference(invitesRef.current, current).forEach((code) => map.removeInvite(code));
    difference(current, invitesRef.current).forEach((code) => map.addInvite(code));
    invitesRef.current = current;
  }, [invites, map]);

  return (
    <div className='glympser-container glympser-container--roundedWithShadow' ref={containerRef} />
  );
}
